import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  AreaChart,
  Area,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";

interface Campaign {
  id: string;
  name: string;
  cost: number;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  conversionValue: number;
  cpa: number;
  roas: number;
}

interface DestinationAds {
  destination: string;
  destinationId: string | null;
  destinationSlug: string;
  totalCost: number;
  totalConversions: number;
  totalRevenue: number;
  roas: number;
  cpa: number;
  profitable: boolean;
}

interface AdsSummary {
  totalSpend: number;
  totalConversions: number;
  totalRevenue: number;
  overallRoas: number;
  avgCpa: number;
}

interface MonthlyAds {
  month: string;
  spend: number;
  conversions: number;
  revenue: number;
}

interface Recommendation {
  destination: string;
  destinationId: string | null;
  issue: string;
  suggestion: string;
  priority: "high" | "medium" | "low";
}

interface AdsPerformanceProps {
  campaigns: Campaign[];
  byDestination: DestinationAds[];
  summary: AdsSummary;
  byMonth: MonthlyAds[];
  recommendations: Recommendation[];
  isLoading?: boolean;
  error?: string | null;
}

const MONTH_NAMES = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čer", "Čec", "Srp", "Zář", "Říj", "Lis", "Pro"];

function formatMonth(month: string): string {
  const parts = month.split("-");
  if (parts.length >= 2) {
    return MONTH_NAMES[parseInt(parts[1], 10) - 1] || parts[1];
  }
  return month;
}

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M Kč`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K Kč`;
  }
  return `${value} Kč`;
}

function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K`;
  }
  return value.toString();
}

function getRoasColor(roas: number): string {
  if (roas >= 5) return "#10b981"; // green
  if (roas >= 2) return "#3b82f6"; // blue
  if (roas >= 1) return "#f59e0b"; // yellow
  return "#ef4444"; // red
}

export default function AdsPerformance({
  campaigns,
  byDestination,
  summary,
  byMonth,
  recommendations,
  isLoading,
  error,
}: AdsPerformanceProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
          <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <p className="text-red-500 dark:text-red-400">{error}</p>
          <p className="text-sm text-gray-500 mt-2">
            Zkontrolujte nastavení Google Ads v environment variables.
          </p>
        </div>
      </div>
    );
  }

  const sortedByRoas = [...byDestination].sort((a, b) => b.roas - a.roas);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Celkové výdaje</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {summary.totalSpend.toLocaleString("cs-CZ")} Kč
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Tržby z reklam</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {summary.totalRevenue.toLocaleString("cs-CZ")} Kč
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">ROAS</p>
          <p
            className={`text-2xl font-bold ${
              summary.overallRoas >= 1 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
            }`}
          >
            {summary.overallRoas.toFixed(1)}x
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Konverze</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {summary.totalConversions.toLocaleString("cs-CZ")}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Prům. CPA</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {summary.avgCpa.toLocaleString("cs-CZ")} Kč
          </p>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Doporučení
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  rec.priority === "high"
                    ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
                    : rec.priority === "medium"
                    ? "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800"
                    : "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`text-lg ${
                      rec.priority === "high" ? "text-red-500" : rec.priority === "medium" ? "text-yellow-500" : "text-blue-500"
                    }`}
                  >
                    {rec.issue.includes("Vysoký ROAS") ? "+" : rec.issue.includes("Nízký") ? "!" : "i"}
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {rec.destinationId ? (
                        <Link
                          to={`/destinations/${rec.destinationId}`}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        >
                          {rec.destination}
                        </Link>
                      ) : (
                        rec.destination
                      )}
                      : {rec.issue}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{rec.suggestion}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ROAS by Destination Chart */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          ROAS podle destinace
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sortedByRoas}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickFormatter={(v) => `${v}x`}
              />
              <YAxis
                type="category"
                dataKey="destination"
                tick={{ fontSize: 11, fill: "#6b7280" }}
                width={95}
              />
              <Tooltip
                formatter={(value: number) => [`${value.toFixed(1)}x`, "ROAS"]}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Bar dataKey="roas" radius={[0, 4, 4, 0]}>
                {sortedByRoas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getRoasColor(entry.roas)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-green-500" /> ROAS 5+
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-blue-500" /> ROAS 2-5
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-yellow-500" /> ROAS 1-2
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-red-500" /> ROAS &lt;1 (ztráta)
          </span>
        </div>
      </div>

      {/* Spend vs Revenue Trend */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Výdaje vs. Tržby v čase
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byMonth} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonth}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                tickFormatter={formatNumber}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
                width={60}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value.toLocaleString("cs-CZ")} Kč`,
                  name === "spend" ? "Výdaje" : "Tržby",
                ]}
                labelFormatter={formatMonth}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend formatter={(value) => (value === "spend" ? "Výdaje" : "Tržby")} />
              <Area
                type="monotone"
                dataKey="spend"
                name="spend"
                stroke="#ef4444"
                strokeWidth={2}
                fill="url(#colorSpend)"
              />
              <Area
                type="monotone"
                dataKey="revenue"
                name="revenue"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#colorRevenue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Destinations Table */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Výkon podle destinace
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 font-medium text-gray-500 dark:text-gray-400">
                  Destinace
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  Náklady
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  Tržby
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  Konverze
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  CPA
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  ROAS
                </th>
                <th className="text-center py-3 font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {byDestination.map((dest) => (
                <tr
                  key={dest.destinationSlug}
                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="py-3">
                    {dest.destinationId ? (
                      <Link
                        to={`/destinations/${dest.destinationId}`}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        {dest.destination}
                      </Link>
                    ) : (
                      <span className="text-gray-900 dark:text-white">{dest.destination}</span>
                    )}
                  </td>
                  <td className="text-right py-3 text-gray-900 dark:text-white">
                    {dest.totalCost.toLocaleString("cs-CZ")} Kč
                  </td>
                  <td className="text-right py-3 text-green-600 dark:text-green-400 font-medium">
                    {dest.totalRevenue.toLocaleString("cs-CZ")} Kč
                  </td>
                  <td className="text-right py-3 text-gray-600 dark:text-gray-400">
                    {dest.totalConversions.toLocaleString("cs-CZ")}
                  </td>
                  <td className="text-right py-3 text-gray-600 dark:text-gray-400">
                    {dest.cpa.toLocaleString("cs-CZ")} Kč
                  </td>
                  <td className="text-right py-3">
                    <span
                      className={`font-medium ${
                        dest.roas >= 2
                          ? "text-green-600 dark:text-green-400"
                          : dest.roas >= 1
                          ? "text-yellow-600 dark:text-yellow-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {dest.roas.toFixed(1)}x
                    </span>
                  </td>
                  <td className="text-center py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        dest.profitable
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {dest.profitable ? "Ziskové" : "Ztrátové"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Kampaně
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 font-medium text-gray-500 dark:text-gray-400">
                  Kampaň
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  Náklady
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  Zobrazení
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  Kliky
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  CTR
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  Konverze
                </th>
                <th className="text-right py-3 font-medium text-gray-500 dark:text-gray-400">
                  ROAS
                </th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr
                  key={campaign.id}
                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="py-3 text-gray-900 dark:text-white">{campaign.name}</td>
                  <td className="text-right py-3 text-gray-900 dark:text-white">
                    {campaign.cost.toLocaleString("cs-CZ")} Kč
                  </td>
                  <td className="text-right py-3 text-gray-600 dark:text-gray-400">
                    {campaign.impressions.toLocaleString("cs-CZ")}
                  </td>
                  <td className="text-right py-3 text-gray-600 dark:text-gray-400">
                    {campaign.clicks.toLocaleString("cs-CZ")}
                  </td>
                  <td className="text-right py-3 text-gray-600 dark:text-gray-400">
                    {campaign.ctr.toFixed(2)}%
                  </td>
                  <td className="text-right py-3 text-gray-600 dark:text-gray-400">
                    {campaign.conversions}
                  </td>
                  <td className="text-right py-3">
                    <span
                      className={`font-medium ${
                        campaign.roas >= 2
                          ? "text-green-600 dark:text-green-400"
                          : campaign.roas >= 1
                          ? "text-yellow-600 dark:text-yellow-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {campaign.roas.toFixed(1)}x
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
